export interface VesselTankSlot {
  slot: string;
  label: string;
}

export interface CargoHeatingVesselRules {
  id: string;
  label: string;
  imoNumbers: string[];
  namePatterns: RegExp[];
  tankSlots: VesselTankSlot[];
  starboardKeyPattern: RegExp;
  compareToleranceC: number;
  heatPlan?: {
    requireDeckHeatExchangerYes?: boolean;
    lockSteamPipesInsulation?: string;
    maxForecastDays?: number;
    requireForecastDays?: boolean;
  };
}

export const DEFAULT_COMPARE_TANK_SLOTS: VesselTankSlot[] = [
  { slot: '1-PORT', label: '1 Port' },
  { slot: '1-STBD', label: '1 Stbd' },
  { slot: '2-PORT', label: '2 Port' },
  { slot: '2-STBD', label: '2 Stbd' },
  { slot: '3-PORT', label: '3 Port' },
  { slot: '3-STBD', label: '3 Stbd' },
  { slot: '4-PORT', label: '4 Port' },
  { slot: '4-STBD', label: '4 Stbd' },
];

export const NORD_VALHALLA_RULES: CargoHeatingVesselRules = {
  id: 'nord-valhalla',
  label: 'Nord Valhalla',
  imoNumbers: ['9985825'],
  namePatterns: [/nord\s*valhalla/i],
  tankSlots: [
    { slot: '1-PORT', label: '1 Port' },
    { slot: '1-PTR', label: '1 Ptr' },
    { slot: '2-PORT', label: '2 Port' },
    { slot: '2-PTR', label: '2 Ptr' },
    { slot: '3-PORT', label: '3 Port' },
    { slot: '3-PTR', label: '3 Ptr' },
    { slot: '4-PORT', label: '4 Port' },
    { slot: '4-PTR', label: '4 Ptr' },
  ],
  starboardKeyPattern: /ptr|stbd|starboard/i,
  compareToleranceC: 1,
  heatPlan: {
    requireDeckHeatExchangerYes: true,
    lockSteamPipesInsulation: 'Yes',
    requireForecastDays: true,
    maxForecastDays: 7,
  },
};

export const FAITHFUL_STAR_RULES: CargoHeatingVesselRules = {
  id: 'faithful-star',
  label: 'Faithful Star',
  imoNumbers: ['9228320'],
  namePatterns: [/faithful\s*star/i],
  tankSlots: [
    { slot: '1-PORT', label: '1 Port' },
    { slot: '1-PTR2', label: '1 Ptr2' },
    { slot: '2-PORT', label: '2 Port' },
    { slot: '2-PTR2', label: '2 Ptr2' },
    { slot: '3-PORT', label: '3 Port' },
    { slot: '3-PTR2', label: '3 Ptr2' },
    { slot: '4-PORT', label: '4 Port' },
    { slot: '4-PTR2', label: '4 Ptr2' },
  ],
  starboardKeyPattern: /ptr2|ptr|stbd|starboard/i,
  compareToleranceC: 1,
  heatPlan: {
    requireForecastDays: true,
    maxForecastDays: 7,
  },
};

const VESSEL_RULES: CargoHeatingVesselRules[] = [
  NORD_VALHALLA_RULES,
  FAITHFUL_STAR_RULES,
];

export function normalizeVesselImo(value: unknown): string {
  return String(value ?? '')
    .replace(/\D/g, '')
    .trim();
}

export function resolveCargoHeatingVesselRules(
  vesselName?: string,
  vesselImo?: string,
): CargoHeatingVesselRules | null {
  const imo = normalizeVesselImo(vesselImo);
  const name = (vesselName || '').trim();

  for (const rules of VESSEL_RULES) {
    if (imo && rules.imoNumbers.includes(imo)) {
      return rules;
    }
  }

  for (const rules of VESSEL_RULES) {
    if (name && rules.namePatterns.some((pattern) => pattern.test(name))) {
      return rules;
    }
  }

  return null;
}

export function getCompareTankSlotsForVessel(
  vesselName?: string,
  vesselImo?: string,
): VesselTankSlot[] {
  return (
    resolveCargoHeatingVesselRules(vesselName, vesselImo)?.tankSlots ??
    DEFAULT_COMPARE_TANK_SLOTS
  );
}

export function getCompareToleranceForVessel(
  vesselName?: string,
  vesselImo?: string,
  fallback = 1,
): number {
  return (
    resolveCargoHeatingVesselRules(vesselName, vesselImo)?.compareToleranceC ??
    fallback
  );
}

export function getStarboardKeyPatternForVessel(
  vesselName?: string,
  vesselImo?: string,
): RegExp {
  return (
    resolveCargoHeatingVesselRules(vesselName, vesselImo)?.starboardKeyPattern ??
    /stbd|ptr|starboard/i
  );
}

export function validateHeatPlanForVessel(
  formValue: Record<string, unknown>,
  vesselName?: string,
  vesselImo?: string,
): string | null {
  const rules = resolveCargoHeatingVesselRules(
    vesselName || String(formValue['vessel_name'] || ''),
    vesselImo || String(formValue['vessel_imo'] || ''),
  );
  if (!rules?.heatPlan) {
    return null;
  }

  const hp = rules.heatPlan;

  if (hp.requireDeckHeatExchangerYes) {
    const deckHeatExchanger = String(formValue['deckHeatExchanger'] || '')
      .trim()
      .toUpperCase();
    if (deckHeatExchanger !== 'YES') {
      return `${rules.label} requires Deck Heat Exchanger to be set to YES.`;
    }
  }

  if (hp.lockSteamPipesInsulation) {
    const insulation = String(formValue['steamPipesInsulation'] || '').trim();
    if (insulation !== hp.lockSteamPipesInsulation) {
      return `${rules.label} requires Steam Pipes Insulation to be "${hp.lockSteamPipesInsulation}".`;
    }
  }

  if (hp.requireForecastDays) {
    const forecastDays = Number(formValue['forecastDays']);
    if (!Number.isFinite(forecastDays) || forecastDays <= 0) {
      return `${rules.label} requires Forecast Days to be selected.`;
    }
    if (hp.maxForecastDays && forecastDays > hp.maxForecastDays) {
      return `${rules.label} allows a maximum of ${hp.maxForecastDays} forecast days.`;
    }
  }

  return null;
}

export function applyHeatPlanVesselRestrictions(
  patch: Record<string, unknown>,
  vesselName?: string,
  vesselImo?: string,
): Record<string, unknown> {
  const rules = resolveCargoHeatingVesselRules(vesselName, vesselImo);
  if (!rules?.heatPlan) {
    return patch;
  }

  const next = { ...patch };

  if (rules.heatPlan.lockSteamPipesInsulation) {
    next['steamPipesInsulation'] = rules.heatPlan.lockSteamPipesInsulation;
  }

  if (rules.heatPlan.requireDeckHeatExchangerYes) {
    next['deckHeatExchanger'] = 'YES';
  }

  return next;
}
